import React, { useState, useRef, useEffect } from "react";
import {
  Menu,
  Bell,
  Search,
  ChevronDown,
  LogOut,
  User,
  Trash2,
  ShieldAlert,
  Command,
  Sparkles,
  Layers,
  Check
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { deleteUser } from "../../../reducer/userSlice";
import useCurrentUser from "../../../hooks/features/auth/useAuth";
import { useProduct } from "../../../hooks/core/useProduct";
import useSites from "../../../hooks/features/sites/useSites";

interface HeaderProps {
  onToggleSidebar: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const productDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, loading } = useCurrentUser();
  const { selectedProduct, changeProduct } = useProduct();
  const { sites: products, loading: sitesLoading } = useSites();

  // Transformer les sites en format produit
  const productList = products.map(site => ({
    value: site.slug,
    label: site.name,
    logo: site.logo || '/default-logo.png',
    is_active: site.is_active,
  }));

  // Fermeture des dropdowns lors d'un clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target as Node)) {
        setIsProductDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.clear();
    dispatch(deleteUser());
    navigate("/login");
  };

  const handleClearCache = () => {
    localStorage.clear();
    setIsDropdownOpen(false);
    window.location.reload();
  };

  const handleProductChange = (productValue: string) => {
    changeProduct(productValue);
    setIsProductDropdownOpen(false);
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return "U";
    const first = firstName?.charAt(0) || "";
    const last = lastName?.charAt(0) || "";
    return (first + last).toUpperCase();
  };

  const initials = getInitials(user?.first_name, user?.last_name);
  const fullName = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "Utilisateur";

  // Récupérer le produit actif
  const currentProduct = productList.find(p => p.value === selectedProduct);
  const currentProductLabel = currentProduct?.label || "Sélectionner un produit";

  // Filtrer les produits actifs pour le dropdown
  const activeProducts = productList.filter(p => p.is_active !== false);

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200/80 px-4 lg:px-6 py-3 transition-all shadow-sm">      
      <div className="flex items-center justify-between gap-4">

        {/* Section Gauche : Toggle Sidebar & Recherche Globale */}
        <div className="flex items-center gap-3 md:gap-4 flex-1 max-w-md">
          <button
            onClick={onToggleSidebar}
            className="p-2 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100/80 active:scale-95 transition-all"
            title="Menu latéral"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Barre de recherche stylisée CMS */}
          <div className="relative w-full hidden sm:block">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Rechercher dans le CMS..."
              className="w-full bg-gray-50/80 hover:bg-gray-100/60 focus:bg-white text-xs text-gray-800 placeholder:text-gray-400 pl-10 pr-10 py-2 rounded-xl border border-gray-200/80 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all shadow-xs"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-0.5 text-[10px] text-gray-400 bg-white border border-gray-200 rounded px-1.5 py-0.5 font-mono shadow-2xs">
              <Command className="w-2.5 h-2.5" />
              <span>K</span>
            </div>
          </div>
        </div>

        {/* Section Droite : Notifications & Profil */}
        <div className="flex items-center gap-2 md:gap-3">

          {/* Sélecteur de Produit */}
          <div className="relative" ref={productDropdownRef}>
            <button
              onClick={() => setIsProductDropdownOpen(!isProductDropdownOpen)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-primary/5 hover:bg-primary/10 border border-primary/20 transition-all text-xs font-medium text-primary"
            >
              <Layers className="w-3.5 h-3.5" />
              <span className="hidden sm:inline max-w-[100px] truncate">
                {currentProductLabel}
              </span>
              <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isProductDropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Dropdown des produits */}
            {isProductDropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150 max-h-[400px] overflow-y-auto">
                <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Changer de plateforme
                  </p>
                  {!sitesLoading && (
                    <button
                      onClick={() => {
                        setIsProductDropdownOpen(false);
                        navigate("/admin/sites/new");
                      }}
                      className="text-[10px] font-medium text-primary hover:text-primary/80 flex items-center gap-1"
                    >
                      + Ajouter
                    </button>
                  )}
                </div>
                
                {sitesLoading ? (
                  <div className="px-4 py-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                    <p className="text-xs text-gray-400 mt-2">Chargement...</p>
                  </div>
                ) : activeProducts.length === 0 ? (
                  <div className="px-4 py-6 text-center">
                    <p className="text-sm text-gray-500">Aucun site disponible</p>
                    <button
                      onClick={() => {
                        setIsProductDropdownOpen(false);
                        navigate("/admin/sites/new");
                      }}
                      className="mt-2 text-xs text-primary hover:text-primary/80 font-medium"
                    >
                      Créer votre premier site
                    </button>
                  </div>
                ) : (
                  activeProducts.map((product) => {
                    const isActive = selectedProduct === product.value;
                    
                    return (
                      <button
                        key={product.value}
                        onClick={() => {
                          if (product.value) {
                            handleProductChange(product.value);
                          }
                        }}
                        className={`
                          w-full px-4 py-2.5 flex items-center gap-3 transition-colors text-left hover:bg-gray-50
                          ${isActive ? 'bg-primary/5' : ''}
                        `}
                      >
                        {/* Logo du produit */}
                        <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center p-1.5 flex-shrink-0">
                          <img 
                            src={product.logo} 
                            alt={product.label}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                        
                        {/* Nom du produit */}
                        <span className={`text-sm font-medium flex-1 ${isActive ? 'text-primary' : 'text-gray-700'}`}>
                          {product.label}
                        </span>
                        
                        {/* Check si actif */}
                        {isActive && (
                          <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Bouton Notification */}
          <button
            className="relative p-2.5 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100/80 transition-all active:scale-95"
            aria-label="Notifications"
          >
            <Bell className="w-4 h-4" />
            <span className="absolute top-2 right-2 w-2 h-2 bg-primary rounded-full ring-2 ring-white animate-pulse" />
          </button>

          <div className="h-5 w-[1px] bg-gray-200 mx-1 hidden sm:block" />

          {/* Dropdown Utilisateur */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-3 p-1.5 pl-2 rounded-xl hover:bg-gray-100/80 transition-all border border-transparent hover:border-gray-200/60"
            >
              {loading ? (
                <div className="flex items-center gap-2 animate-pulse">
                  <div className="w-8 h-8 rounded-lg bg-gray-200" />
                  <div className="hidden md:flex flex-col gap-1">
                    <div className="w-20 h-3 bg-gray-200 rounded" />
                    <div className="w-12 h-2 bg-gray-200 rounded" />
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-8 h-8 rounded-lg bg-primary text-white font-semibold text-xs flex items-center justify-center shadow-xs shrink-0">
                    {initials}
                  </div>

                  <div className="hidden md:flex flex-col text-left min-w-0">
                    <span className="text-xs font-semibold text-gray-800 truncate leading-tight">
                      {fullName}
                    </span>
                    <span className="text-[10px] font-medium text-gray-400 capitalize truncate">
                      {user?.role?.toLowerCase() || "Utilisateur"}
                    </span>
                  </div>

                  <ChevronDown
                    className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? "rotate-180 text-primary" : ""}`}
                  />
                </>
              )}
            </button>

            {/* Menu Déroulant Profil */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-b from-gray-50/50 to-white">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-bold text-gray-900 truncate">{fullName}</p>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase bg-primary/10 text-primary">
                      {user?.role || "USER"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 truncate mb-2.5">{user?.email || "Non renseigné"}</p>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${user?.status === "ACTIVE" ? "bg-emerald-500" : "bg-amber-500"}`} />
                    <span className="text-[11px] font-medium text-gray-500">
                      Compte {user?.status === "ACTIVE" ? "Actif" : "Restreint"}
                    </span>
                  </div>
                </div>

                <div className="p-1.5">
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate("/admin/profile");
                    }}
                    className="w-full px-3 py-2 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 flex items-center gap-2.5 transition-colors"
                  >
                    <User className="w-4 h-4 text-gray-400" />
                    Mon Profil & Paramètres
                  </button>
                </div>

                <div className="p-1.5 border-t border-gray-100">
                  <button
                    onClick={handleClearCache}
                    className="w-full px-3 py-2 rounded-lg text-xs font-medium text-amber-700 hover:bg-amber-50/80 flex items-center gap-2.5 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-amber-500" />
                    Réinitialiser le cache local
                  </button>
                </div>

                <div className="p-1.5 border-t border-gray-100">
                  <button
                    onClick={handleLogout}
                    className="w-full px-3 py-2 rounded-lg text-xs font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-2.5 transition-colors"
                  >
                    <LogOut className="w-4 h-4 text-rose-500" />
                    Se déconnecter
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>

      </div>
    </header>
  );
};